import React, { memo } from 'react';
import { View } from 'react-native';

import { SvgIcon, Text } from '@/components/ui';
import { useCoachNotificationUnreadCount } from '@/features/coach/hooks/use-coach-notifications';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';

type CoachNotificationsTabIconProps = {
  focused: boolean;
};

/**
 * Coach Notifications tab icon with a numeric unread badge (top-right), matching the
 * design: red pill + white count over the bell.
 */
function CoachNotificationsTabIcon({ focused }: CoachNotificationsTabIconProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const unreadCount = useCoachNotificationUnreadCount();
  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <View style={styles.iconWrap}>
      <SvgIcon name="IcNotificationBing" size={scale(24)} color={focused ? 'white' : colors.Blue} />
      {unreadCount > 0 ? (
        <View style={[styles.badge, unreadCount > 9 ? styles.badgeWide : null]}>
          <Text variant="Font Inter/Subtitle/Bold 10px" numberOfLines={1} style={styles.badgeText}>
            {badgeLabel}
          </Text>
        </View>
      ) : null}
    </View>
  );
}

const useStyles = createStyles(({ scale }) => ({
  // Extra room so the unread badge isn't clipped by the tab button's `overflow: 'hidden'`.
  iconWrap: {
    width: scale(28),
    height: scale(28),
    alignItems: 'center',
    justifyContent: 'center',
  },
  badge: {
    position: 'absolute',
    top: scale(-2),
    right: scale(-2),
    minWidth: scale(16),
    height: scale(16),
    paddingHorizontal: scale(3),
    borderRadius: scale(8),
    borderColor: colors['0'],
    backgroundColor: colors.Red,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeWide: {
    right: scale(-6),
    paddingHorizontal: scale(4),
  },
  badgeText: {
    color: colors['0'],
    fontSize: scale(9),
    lineHeight: scale(11),
    textAlign: 'center',
  },
}));

export default memo(CoachNotificationsTabIcon);
